import React, { useEffect, useRef } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { CenterFrameLayout } from 'Layouts';
import { SecondaryText } from 'Components/Texts';
import { trackEvent, TRACKING_ENUMS } from 'Utils/Analytics';
import {
  FINISH_VERIFYING_SHEER_EMAIL_ID,
  GIFTING_QUERY_PARAM,
  UNSUCCESSFUL_SHEER_VERIFY_EMAIL_ID,
  VA_QUERY_PARAM,
} from 'Utils/Constants';
import { checkIfShowSheerBanner, isDefaultPlanFlow } from 'Utils/Analytics/Amplitude';
import { SheerIdBanner } from 'Features/SheerIdPromoBanner/SheerIdBanner';
import { getSheerProgramFromSKU } from 'Utils/SheerIdHelpers';
import { encodeSearchParams } from 'Utils/Path';
import { createUserAccount, handleSignInSuccess, handleSignUpSuccess } from 'Utils/SignInHelpers';
import CreateAccountForm from 'Forms/CreateAccountForm';
import { getPlans } from 'State/Plans';
import { useStore } from 'Hooks/useStore';
import './CreateContainer.css';

export const CreateContainer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { preSelectedPlan = '' } = useParams();

  const { hasSubscription, isFetching, isStatusSuccess, isSuccess, isIAPUser, hasActiveSubscription } =
    useStore('subscription');

  const { routes, defaultConfig } = useStore('appConfig');
  const user = useStore('user');
  const { bango, sheerId } = useStore('integrations');
  const pageStartSource = useStore('tracking.pageStartSource');
  const { requirePlanSelection, isGifting } = useStore('purchaseMode');
  const { hasShippingAddress } = useStore('checkout');
  const { isPlanSelected, selectedPlan, selectPlanActionComplete } = useStore('plans');

  const { verificationId } = sheerId;
  const isSheerIdVerification = (location?.state?.from?.pathname || '').includes(routes.sheerIdVerification);
  const isBango = bango.subscriptionId;

  //This is used so the redirects don't trigger after account creation on this page
  const isAuthenticatedOnFirstRender = useRef(user.isAuthenticated);

  const isLoading =
    (isFetching && !isStatusSuccess) || (requirePlanSelection && !selectPlanActionComplete && !isPlanSelected);

  const urlParams = new URLSearchParams(location.search);

  const bangoProductId = bango.productId;
  const sheerProgramType = urlParams.get('sheerVerification') || getSheerProgramFromSKU(bangoProductId);
  const isVeteransAdv = urlParams.get(VA_QUERY_PARAM);

  const brazeDefaultParam = urlParams.get('lid');
  const emailCampaignId = urlParams.get('cmpid');
  const fromMilitaryEmailDeeplink =
    !!(brazeDefaultParam && emailCampaignId) &&
    [FINISH_VERIFYING_SHEER_EMAIL_ID, UNSUCCESSFUL_SHEER_VERIFY_EMAIL_ID].some((id) => {
      return emailCampaignId.includes(id);
    });

  useEffect(() => {
    if (!isLoading && !user.isAuthenticated) {
      trackEvent(TRACKING_ENUMS.EVENT_SIGNUP_STARTED);
    }
  }, [isLoading, user.isAuthenticated]);

  useEffect(() => {
    //Needed since sometimes other pages redirect to this page with a preSelectedPlan added in url
    if (!fromMilitaryEmailDeeplink) getPlans(preSelectedPlan);
  }, [preSelectedPlan, fromMilitaryEmailDeeplink]);

  // redirect logic for already logged in user when component first renders
  if (isAuthenticatedOnFirstRender.current && isSuccess) {
    // if both subscription status (isStatusSuccess) and subscription data (isSuccess and hasSubscription) are loaded
    //    if subscription active, redirect to either "already currently subscribed" page
    //    (to military variant of page if isIAPUser)
    //    otherwise redirect to change plans page
    if (isStatusSuccess && hasSubscription && !isBango) {
      if (verificationId) {
        if (isIAPUser && hasActiveSubscription) {
          return (
            <Navigate replace to={{ search: location.search, pathname: routes.signupCurrentSubscriberMilitaryFirst }} />
          );
        }
        const param = preSelectedPlan ? '/' + preSelectedPlan : '';
        return <Navigate replace to={{ search: location.search, pathname: routes.changePlans + param }} />;
      }
      return <Navigate replace to={{ search: location.search, pathname: routes.signupCurrentSubscriber }} />;
    }
    // redirection logic for bango/sheer if user does not have subscription
    if (selectPlanActionComplete) {
      if (isPlanSelected && bango.subscriptionId) {
        return <Navigate replace to={routes.bangoFinal} />;
      } else if (isPlanSelected) {
        return <Navigate replace to={routes.payment} />;
      } else if (!bango.subscriptionId && !sheerProgramType) {
        return <Navigate replace to={routes.signupPlans} />;
      } else if (sheerProgramType) {
        return <Navigate replace to={`${routes.sheerIdVerification}?sheerVerification=${sheerProgramType}`} />;
      }
    }
  }

  if (selectedPlan?.hasMerchandise && !hasShippingAddress) {
    return <Navigate replace to={routes.shipping} />;
  }

  // if from email deeplink, we want to directly take the user to sign in instead of create account
  if (fromMilitaryEmailDeeplink && sheerProgramType) {
    const searchParams = encodeSearchParams({ return_to: `${routes.sheerIdVerification}${location.search}` });
    const url = `${routes.signin}?${searchParams}`;

    return <Navigate replace to={url} />;
  }

  // if not logged in and if no plan selected (except for bango/sheer/veterans adv users), redirect to select plan
  if (
    requirePlanSelection &&
    selectPlanActionComplete &&
    !isPlanSelected &&
    !preSelectedPlan &&
    !isBango &&
    !isSheerIdVerification &&
    !isVeteransAdv
  ) {
    return <Navigate replace to={routes.signupPlans} />;
  }

  let searchParam;
  if (isGifting) {
    searchParam = `${GIFTING_QUERY_PARAM}`;
  } else if (isVeteransAdv) {
    searchParam = `${VA_QUERY_PARAM}=${isVeteransAdv}`;
  } else {
    const searchParamObj = new URLSearchParams();
    searchParamObj.set('return_to', `${location.pathname}${location.search}`); //create account screen including params
    searchParam = searchParamObj.toString();
  }

  let title = 'Checking your subscription';
  if (!isLoading) {
    title = isGifting ? 'Create Your Account To Redeem' : 'Create Your Account';
  }

  const { enableSheerIdBanner } = defaultConfig;
  const showBanner = (isDefaultPlanFlow() && !isBango && !isGifting && enableSheerIdBanner) || checkIfShowSheerBanner();

  const onSignInSuccess = handleSignInSuccess(location.pathname);
  const onSignUpSuccess = handleSignUpSuccess(isSheerIdVerification, isVeteransAdv, urlParams, (path) => {
    navigate(path);
  });

  return (
    <CenterFrameLayout
      title={title}
      showLoader={isLoading}
      modernLayout
      customFrameClass={showBanner && 'frameWithSheerIdBanner'}
    >
      {showBanner && <SheerIdBanner />}
      <CreateAccountForm
        submitButtonText="Continue"
        pageStartSource={pageStartSource}
        onFormSubmit={(values) => createUserAccount(values, onSignInSuccess, onSignUpSuccess)}
      />
      <SecondaryText
        key="b"
        alignText="centerText"
        text={[
          'Already have an account? ',
          <Link
            key="0"
            to={{
              pathname: routes.signin,
              search: searchParam,
            }}
            className="blueLink"
          >
            Sign in
          </Link>,
        ]}
      />
    </CenterFrameLayout>
  );
};
